import { Injectable } from '@angular/core';
import { ApiService } from '../core/services/api.service';
import { HttpResponse } from '@angular/common/http';
import { AssignProjectTemplatesRequest, AssignTemplatesToSkidsRequest, BlockScheduleFileModel, CloneProjectTemplateRequest, ProjectTemplateModel, ProjectTemplateSkidAssignmentModel, SaveSkidChecklistRequest, SaveSkidChecklistResponse, SkidChecklistResponseModel, UploadFileResponse } from '../models/project.model';
import { Observable } from 'rxjs';
import { LineItemModel, ProjectTemplateSkidStatusModel, ReferenceImageModel, TemplateSectionModel } from '../models/template.model';

@Injectable({
  providedIn: 'root'
})
export class ProjecttemplateService {
  private baseUrl = 'ProjectTemplate';

  constructor(private apiService: ApiService) { }

  private formatDateOnly(value?: string | Date | null): string | undefined {
    if (!value) {
      return undefined;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime())
        ? undefined
        : this.formatDateParts(value.getFullYear(), value.getMonth() + 1, value.getDate());
    }

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return this.formatDateParts(Number(match[1]), Number(match[2]), Number(match[3]));
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
      ? undefined
      : this.formatDateParts(parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate());
  }

  private formatDateParts(year: number, month: number, day: number): string {
    return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
  }
  // Project Template Assignment  
  getProjectTemplates(projectId: number): Observable<ProjectTemplateModel[]> {
    return this.apiService.get(`${this.baseUrl}/GetProjectTemplates/${projectId}`);
  }

  getProjectTemplatesByType(projectId: number, typeId: number): Observable<ProjectTemplateModel[]> {
    return this.apiService.get(`${this.baseUrl}/GetProjectTemplatesByType/${projectId}/${typeId}`);
  }

  getProjectTemplateById(templateId: number): Observable<ProjectTemplateModel> {
    return this.apiService.get(`${this.baseUrl}/GetProjectTemplateById/${templateId}`);
  }

  getProjectTemplatesByUserRole(projectId: number, typeId: number): Observable<ProjectTemplateModel[]> {
   return this.apiService.get(`${this.baseUrl}/GetProjectTemplatesByUserRole/${projectId}/${typeId}`);
 }

  getProjectTemplatesDataByUserRole(projectId: number, typeId: number): Observable<ProjectTemplateModel[]> {
    return this.apiService.get(`${this.baseUrl}/GetProjectTemplatesDataByUserRole/${projectId}/${typeId}`);
  }

  getProjectTemplatesDataByUserRoleAndSkid(projectId: number, typeId: number, blockScheduleId: number): Observable<ProjectTemplateModel[]> {
    return this.apiService.get(`${this.baseUrl}/GetProjectTemplatesDataByUserRoleAndSkid/${projectId}/${typeId}/${blockScheduleId}`);
  }

  getSkidTemplateAssignmentsByUserRole(projectId: number, typeId: number): Observable<ProjectTemplateSkidAssignmentModel[]> {
    return this.apiService.get(`${this.baseUrl}/GetSkidTemplateAssignmentsByUserRole/${projectId}/${typeId}`);
  }


  getProjectTemplateDataByTemplateId(templateId: number): Observable<ProjectTemplateModel> {
    return this.apiService.get(`${this.baseUrl}/GetProjectTemplateDataByTemplateId/${templateId}`);
  }

  getProjectTemplateRoles(projectTemplateId: number): Observable<number[]> {
    return this.apiService.get(`${this.baseUrl}/GetProjectTemplateRoles/${projectTemplateId}`);
  }

  getProjectTemplateSectionList(templateId: number): Observable<TemplateSectionModel[]> {
    return this.apiService.get(`${this.baseUrl}/GetProjectTemplateSectionList/${templateId}`);
  }

  deleteProjectTemplateSection(sectionId: number): Observable<any> {
    return this.apiService.delete(`${this.baseUrl}/DeleteProjectTemplateSection/${sectionId}`);
  }

  assignProjectTemplates(request: AssignProjectTemplatesRequest): Observable<any> {
    return this.apiService.post(`${this.baseUrl}/AssignProjectTemplates`, request);
  }

  cloneProjectTemplate(request: CloneProjectTemplateRequest): Observable<number> {
    return this.apiService.post(`${this.baseUrl}/CloneProjectTemplate`, request);
  }

  unassignProjectTemplate(projectId: number, templateId: number): Observable<any> {
    return this.apiService.delete(`${this.baseUrl}/UnassignProjectTemplate/${projectId}/${templateId}`);
  }

  // Checklist - Get project templates with full hierarchy
  getProjectTemplatesData(projectId: number): Observable<ProjectTemplateModel[]> {
    return this.apiService.get(`${this.baseUrl}/GetProjectTemplatesData/${projectId}`);
  }

  // Skid Checklist Response
  saveSkidChecklistResponses(request: SaveSkidChecklistRequest): Observable<SaveSkidChecklistResponse> {
    return this.apiService.post(`${this.baseUrl}/SaveSkidChecklistResponses`, request);
  }

  sendEwmrEmail(projectFormId: number, projectTemplateId: number): Observable<any> {
    return this.apiService.post(`${this.baseUrl}/SendEwmrEmail`, { projectFormId, projectTemplateId });
  }

  sendImpactedAreaNotification(projectId: number, projectTemplateId: number, blockScheduleId?: number, projectFormId?: number): Observable<any> {
    return this.apiService.post(`${this.baseUrl}/SendImpactedAreaNotification`, {
      projectId,
      projectTemplateId,
      blockScheduleId,
      projectFormId
    });
  }

  getSkidChecklistResponses(blockScheduleId: number, projectId: number): Observable<SkidChecklistResponseModel[]> {
    return this.apiService.get(`${this.baseUrl}/GetSkidChecklistResponses/${blockScheduleId}/${projectId}`);
  }

  getSkidChecklistResponsesByProjectFormId(projectFormId: number, projectId: number): Observable<SkidChecklistResponseModel[]> {
    return this.apiService.get(`${this.baseUrl}/GetSkidChecklistResponsesByProjectFormId/${projectFormId}/${projectId}`);
  }

  // Block Schedule File Upload
  uploadFile(blockScheduleId: number, skidChecklistResponseId: number, files: File[]): Observable<UploadFileResponse> {
    const formData = new FormData();
    formData.append('blockScheduleId', blockScheduleId.toString());
    formData.append('skidChecklistResponseId', skidChecklistResponseId.toString());

    files.forEach(file => {
      formData.append('files', file, file.name);
    });

    return this.apiService.post('BlockScheduleFile/UploadFile', formData);
  }

  getFiles(blockScheduleId: number, skidChecklistResponseId?: number): Observable<BlockScheduleFileModel[]> {
    const url = skidChecklistResponseId
      ? `BlockScheduleFile/GetFiles/${blockScheduleId}/${skidChecklistResponseId}`
      : `BlockScheduleFile/GetFiles/${blockScheduleId}`;
    return this.apiService.get(url);
  }

  deleteFile(fileId: number): Observable<any> {
    return this.apiService.delete(`BlockScheduleFile/DeleteFile/${fileId}`);
  }

  downloadChecklistAttachment(fileId: number, optimized = false): Observable<HttpResponse<Blob>> {
    return this.apiService.getBlobResponse(`BlockScheduleFile/DownloadFile/${fileId}?optimized=${optimized}`);
  }

  addProjectTemplate(template: ProjectTemplateModel): Observable<number> {
    return this.apiService.post(`${this.baseUrl}/AddProjectTemplateWithProject`, template);
  }
  updateProjectTemplate(template: ProjectTemplateModel): Observable<ProjectTemplateModel> {
    return this.apiService.put(`${this.baseUrl}/UpdateProjectTemplateWithProject`, template);
  }

  updateProjectTemplateActive(templateId: number, isActive: boolean, updatedBy?: number): Observable<any> {
    return this.apiService.put(`${this.baseUrl}/UpdateProjectTemplateActive/${templateId}`, { isActive, updatedBy });
  }

  deleteProjectTemplate(templateId: number): Observable<any> {
    return this.apiService.delete(`${this.baseUrl}/DeleteProjectTemplate/${templateId}`);
  }

  addProjectTemplateSection(section: TemplateSectionModel): Observable<number> {
    return this.apiService.post(`${this.baseUrl}/AddProjectTemplateSection`, section);
  }

  updateProjectTemplateSection(section: TemplateSectionModel): Observable<TemplateSectionModel> {
    return this.apiService.put(`${this.baseUrl}/UpdateProjectTemplateSection`, section);
  }

  addProjectTemplateLineItem(lineItem: LineItemModel): Observable<number> {
    return this.apiService.post(`${this.baseUrl}/AddProjectTemplateLineItem`, lineItem);
  }

  uploadReferenceImages(projectTemplateId: number, files: File[]): Observable<ReferenceImageModel[]> {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file, file.name));
    return this.apiService.post(`${this.baseUrl}/UploadReferenceImages/${projectTemplateId}`, formData);
  }

  updateProjectTemplateLineItem(lineItem: LineItemModel): Observable<LineItemModel> {
    return this.apiService.put(`${this.baseUrl}/UpdateProjectTemplateLineItem`, lineItem);
  }

  deleteProjectTemplateLineItem(lineItemId: number): Observable<any> {
    return this.apiService.delete(`${this.baseUrl}/DeleteProjectTemplateLineItem/${lineItemId}`);
  }

  batchUpdateProjectLineItemSortOrder(items: { lineItemId: number; sortOrder: number; updatedBy?: number }[]): Observable<any> {
    return this.apiService.post(`${this.baseUrl}/BatchUpdateProjectLineItemSortOrder`, items);
  }

  batchUpdateProjectTemplateSectionSortOrder(items: { sectionId: number; sortOrder: number; updatedBy?: number }[]): Observable<any> {
    return this.apiService.post(`${this.baseUrl}/BatchUpdateProjectTemplateSectionSortOrder`, items);
  }

  batchUpdateProjectTemplateSortOrder(items: { templateId: number; sortOrder: number; updatedBy?: number }[]): Observable<any> {
    return this.apiService.post(`${this.baseUrl}/BatchUpdateProjectTemplateSortOrder`, items);
  }

  downloadChecklistPdf(blockScheduleId: number, projectId: number, templateId: number): Observable<Blob> {
    return this.apiService.getBlob(`${this.baseUrl}/DownloadChecklistPdf/${blockScheduleId}/${projectId}/${templateId}`);
  }

  downloadChecklistPdfByProjectForm(projectFormId: number, projectId: number, templateId: number): Observable<Blob> {
    return this.apiService.getBlob(`${this.baseUrl}/DownloadChecklistPdfByProjectForm/${projectFormId}/${projectId}/${templateId}`);
  }

  // Template Skid Status
  getTemplateSkidStatus(blockScheduleId: number, projectTemplateId: number): Observable<ProjectTemplateSkidStatusModel | null> {
    return this.apiService.get(`${this.baseUrl}/GetTemplateSkidStatus?blockScheduleId=${blockScheduleId}&projectTemplateId=${projectTemplateId}`);
  }

  upsertTemplateSkidStatus(model: ProjectTemplateSkidStatusModel): Observable<any> {
    const payload: ProjectTemplateSkidStatusModel = {
      ...model,
      date: this.formatDateOnly(model.date),
      duedate: this.formatDateOnly(model.duedate)
    };

    return this.apiService.post(`${this.baseUrl}/UpsertTemplateSkidStatus`, payload);
  }

  assignTemplatesToSkids(request: AssignTemplatesToSkidsRequest): Observable<any> {
    return this.apiService.post(`${this.baseUrl}/AssignTemplatesToSkids`, request);
  }

  unassignTemplateFromSkid(projectId: number, blockScheduleId: number, projectTemplateId: number): Observable<any> {
    return this.apiService.delete(`${this.baseUrl}/UnassignTemplateFromSkid/${projectId}/${blockScheduleId}/${projectTemplateId}`);
  }

}
